import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AppLink } from "../lib/route-primitives";
import { adminPlansApi } from "../lib/api";
import {
  Loader2, Plus, Pencil, Trash2, Check, X,
  ChevronDown, ChevronUp, Users, HardDrive, Image, Sparkles,
  Repeat, MessageSquareText, Globe, Lock, RotateCcw, CheckCircle2, XCircle, Info, Banknote,
} from "lucide-react";
import { formatTry, isSitePlan } from "@wedding/shared";
import { cn } from "../lib/utils";
import { paths } from "../lib/paths";
import { getPlanColors } from "../lib/plan-colors";

/**
 * Paket TANIMI (2026-10-16): paketin TÜM sayıları + fiyatı buradan — site kartı, satın alma,
 * manuel satış, "Paket ver" hep bu satırı okur. Eski "maks. düğün" alanı yok (paket düğün başına).
 * `sold` = satılmış paket (sipariş + Free/geçiş hibesi). Satılmış paket SÖZLEŞMEDİR (2026-10-17,
 * piyasa uygulaması): tanım değişikliği yalnız yeni satışlara uygulanır, satılmışlar aldığıyla kalır.
 */
interface PlanRow {
  id: string; slug: string; label: string; sort_order: number;
  max_guests_per_wedding: number;
  max_storage_bytes: number; max_media_per_wedding: number;
  max_reminder_sends: number; max_sms_segments: number;
  is_default: number; is_active: number;
  /** Migration 059 yoksa undefined. `null` → teklifle (online satılmaz). */
  price_try?: number | null;
  sold?: number;
  created_at: string; updated_at: string;
}
type Limits = { maxGuestsPerWedding: number; maxStorageBytes: number; maxMediaPerWedding: number; maxReminderSends: number; maxSmsSegments: number };
const LIMIT_FIELDS: Array<{ key: keyof Limits; db: keyof PlanRow; label: string }> = [
  { key: "maxGuestsPerWedding", db: "max_guests_per_wedding", label: "Maks. Misafir" },
  { key: "maxStorageBytes", db: "max_storage_bytes", label: "Depolama" },
  { key: "maxMediaPerWedding", db: "max_media_per_wedding", label: "Maks. Medya" },
  { key: "maxReminderSends", db: "max_reminder_sends", label: "Maks. Tur" },
  { key: "maxSmsSegments", db: "max_sms_segments", label: "Maks. SMS Adedi" },
];
/** Fiyat girişi: boş → `null` (teklifle). */
const priceOf = (v: string): number | null => (v.trim() === "" ? null : Math.max(0, Math.round(Number(v))));
/** Sunucu hata kodu → çeviri anahtarı. */
const PLAN_ERRORS: Record<string, string> = {
  site_plan: "plansAdmin.sitePlanLocked",
  default_locked: "plansAdmin.errors.defaultLocked",
  default_price: "plansAdmin.errors.defaultPrice",
  price_zero: "plansAdmin.errors.priceZero",
  migration_required: "plansAdmin.errors.migrationRequired",
  invalid: "plansAdmin.errors.invalid",
};
interface ChangeLogRow {
  id: string; plan_id: string; changed_by: string;
  change_type: string; old_values: string | null;
  new_values: string | null; created_at: string;
}
interface UsageCount { plan: string; count: number; }

function formatBytes(b: number) {
  if (b >= 1073741824) return `${(b / 1073741824).toFixed(1)} GB`;
  if (b >= 1048576) return `${(b / 1048576).toFixed(0)} MB`;
  return `${b} B`;
}
function bytesFromGB(gb: number) { return Math.round(gb * 1073741824); }
function gbFromBytes(bytes: number) { return +(bytes / 1073741824).toFixed(2); }


const INPUT_CLS = "w-full bg-background/60 border border-white/10 focus-visible:border-primary/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 transition-[border-color,box-shadow]";
const LABEL_CLS = "block text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70 mb-1.5";
const emptyForm = {
  slug: "", label: "", sortOrder: 0,
  maxGuestsPerWedding: 50,
  maxStorageBytes: bytesFromGB(2), maxMediaPerWedding: 100,
  // ⚠️ KÖK NEDEN (2026-07): bu alanlar burada YOKTU → POST'ta hiç gönderilmiyordu
  // → Zod `.default(1)` devreye giriyordu → admin panelinden oluşturulan HER plan
  // sessizce FREE kotasını alıyordu. `enterprise = 1 tur` bunun kanıtıydı.
  // Yeni bir plan alanı eklerken BURAYA da eklemeyi unutma.
  maxReminderSends: 3, maxSmsSegments: 600,
  isDefault: false,
  /** Boş → teklifle (online satılmaz). */
  price: "",
};
type CreateForm = typeof emptyForm;

// ── Create Modal ───────────────────────────────────────────────
function CreateModal({ onClose, onSave }: {
  onClose: () => void;
  onSave: (d: CreateForm) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [d, setD] = useState<CreateForm>({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  async function go() {
    if (!d.slug || !d.label || saving) return;
    setSaving(true);
    await onSave(d);
    setSaving(false);
  }

  return (
    <div ref={ref} onClick={(e) => e.target === ref.current && onClose()}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <p className="text-sm font-semibold">Yeni Plan</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={LABEL_CLS}>Slug</label>
              <input className={INPUT_CLS} placeholder="enterprise" value={d.slug}
                onChange={(e) => setD({ ...d, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })} /></div>
            <div><label className={LABEL_CLS}>Etiket</label>
              <input className={INPUT_CLS} placeholder="Enterprise" value={d.label}
                onChange={(e) => setD({ ...d, label: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label htmlFor="plan-new-price" className={LABEL_CLS}>{t("plansAdmin.price")}</label>
              <input id="plan-new-price" type="number" min={1} inputMode="numeric" className={INPUT_CLS} value={d.price}
                placeholder={t("plansAdmin.priceQuote")} disabled={d.isDefault}
                onChange={(e) => setD({ ...d, price: e.target.value })} /></div>
            <div><label className={LABEL_CLS}>Maks. Misafir</label>
              <input type="number" className={INPUT_CLS} value={d.maxGuestsPerWedding}
                onChange={(e) => setD({ ...d, maxGuestsPerWedding: +e.target.value })} /></div>
          </div>
          <p className="-mt-2 text-[11px] text-muted-foreground">{d.isDefault ? t("plansAdmin.priceDefault") : t("plansAdmin.priceHint")}</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={LABEL_CLS}>Depolama (GB)</label>
              <input type="number" step="0.5" className={INPUT_CLS} value={gbFromBytes(d.maxStorageBytes)}
                onChange={(e) => setD({ ...d, maxStorageBytes: bytesFromGB(+e.target.value) })} /></div>
            <div><label className={LABEL_CLS}>Maks. Medya</label>
              <input type="number" className={INPUT_CLS} value={d.maxMediaPerWedding}
                onChange={(e) => setD({ ...d, maxMediaPerWedding: +e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={LABEL_CLS}>Maks. Tur</label>
              <input type="number" className={INPUT_CLS} value={d.maxReminderSends}
                onChange={(e) => setD({ ...d, maxReminderSends: +e.target.value })} /></div>
            <div><label className={LABEL_CLS}>Maks. SMS Adedi</label>
              <input type="number" className={INPUT_CLS} value={d.maxSmsSegments}
                onChange={(e) => setD({ ...d, maxSmsSegments: +e.target.value })} /></div>
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={d.isDefault}
              onChange={(e) => setD({ ...d, isDefault: e.target.checked })}
              className="rounded border-border text-primary cursor-pointer" />
            <span className="text-sm text-muted-foreground">Varsayılan plan olarak işaretle</span>
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted transition-colors cursor-pointer">İptal</button>
          <button onClick={go} disabled={!d.slug || !d.label || saving}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-40 cursor-pointer shadow-md shadow-primary/30">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Oluştur
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Plan Card ──────────────────────────────────────────────────
function PlanCard({ plan, usageCount, onUpdate, onDelete, onReactivate, lang }: {
  plan: PlanRow; usageCount: number;
  onUpdate: (id: string, d: object) => Promise<void>;
  onDelete: (id: string, slug: string) => Promise<void>;
  onReactivate: (id: string) => Promise<void>;
  lang: string;
}) {
  const { t } = useTranslation();
  const theme = getPlanColors(plan.slug);
  // Tanıtım sitesindeki üç paket (Free/Pro/Max) — silinemez; diğerleri özel.
  const onSite = isSitePlan(plan.slug);
  const [modal, setModal] = useState<"edit" | "delete" | "history" | null>(null);
  const [saving, setSaving]   = useState(false);
  const [history, setHistory] = useState<ChangeLogRow[]>([]);
  const [loadingH, setLoadingH] = useState(false);

  const sold = plan.sold ?? 0;
  const hasPrice = plan.price_try !== undefined; // 059 uygulanmış mı
  const priceText = (v: number | null | undefined) => (v === null || v === undefined ? "" : String(v));
  const [ed, setEd] = useState({
    label: plan.label,
    price: priceText(plan.price_try),
    maxGuestsPerWedding: plan.max_guests_per_wedding,
    maxStorageBytes: plan.max_storage_bytes,
    maxMediaPerWedding: plan.max_media_per_wedding,
    maxReminderSends: plan.max_reminder_sends,
    maxSmsSegments: plan.max_sms_segments,
  });

  const priceChanged = hasPrice && !plan.is_default && ed.price !== priceText(plan.price_try);
  const unchanged =
    ed.label === plan.label && !priceChanged &&
    LIMIT_FIELDS.every((f) => ed[f.key] === Number(plan[f.db]));

  async function saveEdit() {
    setSaving(true);
    const { price, ...rest } = ed;
    await onUpdate(plan.id, priceChanged ? { ...rest, priceTry: priceOf(price) } : rest);
    setModal(null);
    setSaving(false);
  }

  async function confirmDelete() {
    setModal(null);
    await onDelete(plan.id, plan.slug);
  }

  async function openHistory() {
    setModal("history");
    setLoadingH(true);
    try {
      const res = await adminPlansApi.get<{ history: ChangeLogRow[] }>(`/${plan.id}`);
      setHistory(res.data?.history ?? []);
    } catch { /* ignore */ }
    setLoadingH(false);
  }

  const closeModal = () => setModal(null);

  return (
    <>
      {/* ── Edit Modal ── */}
      {modal === "edit" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div>
                <p className="text-sm font-semibold">Plan Düzenle</p>
                <p className="text-xs text-muted-foreground font-mono mt-0.5">/{plan.slug}</p>
              </div>
              <button onClick={closeModal} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className={LABEL_CLS}>Etiket</label>
                <input className={INPUT_CLS} value={ed.label} onChange={(e) => setEd({ ...ed, label: e.target.value })} />
              </div>
              {hasPrice && (
                <div>
                  <label htmlFor={`plan-price-${plan.slug}`} className={LABEL_CLS}>{t("plansAdmin.price")}</label>
                  <input id={`plan-price-${plan.slug}`} type="number" min={1} inputMode="numeric" className={INPUT_CLS}
                    value={plan.is_default ? "0" : ed.price} disabled={!!plan.is_default}
                    placeholder={t("plansAdmin.priceQuote")}
                    onChange={(e) => setEd({ ...ed, price: e.target.value })} />
                  <p className="mt-1 text-[11px] text-muted-foreground">{plan.is_default ? t("plansAdmin.priceDefault") : t("plansAdmin.priceHint")}</p>
                </div>
              )}
              {sold > 0 && (
                <p data-testid={`plan-sold-note-${plan.slug}`} className="rounded-lg border border-amber-500/30 bg-amber-500/8 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                  {t("plansAdmin.soldNote", { n: sold })}
                </p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className={LABEL_CLS}>Maks. Misafir</label>
                  <input type="number" className={INPUT_CLS} value={ed.maxGuestsPerWedding}
                    onChange={(e) => setEd({ ...ed, maxGuestsPerWedding: +e.target.value })} /></div>
                <div><label className={LABEL_CLS}>Depolama (GB)</label>
                  <input type="number" step="0.5" className={INPUT_CLS} value={gbFromBytes(ed.maxStorageBytes)}
                    onChange={(e) => setEd({ ...ed, maxStorageBytes: bytesFromGB(+e.target.value) })} /></div>
                <div><label className={LABEL_CLS}>Maks. Medya</label>
                  <input type="number" className={INPUT_CLS} value={ed.maxMediaPerWedding}
                    onChange={(e) => setEd({ ...ed, maxMediaPerWedding: +e.target.value })} /></div>
                <div><label className={LABEL_CLS}>Maks. Tur</label>
                  <input type="number" className={INPUT_CLS} value={ed.maxReminderSends}
                    onChange={(e) => setEd({ ...ed, maxReminderSends: +e.target.value })} /></div>
                <div><label className={LABEL_CLS}>Maks. SMS Adedi</label>
                  <input type="number" className={INPUT_CLS} value={ed.maxSmsSegments}
                    onChange={(e) => setEd({ ...ed, maxSmsSegments: +e.target.value })} /></div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 pb-5">
              <button onClick={closeModal} className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted transition-colors cursor-pointer">İptal</button>
              <button onClick={saveEdit}
                disabled={saving || unchanged}
                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-sm shadow-primary/20">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Modal ── */}
      {modal === "delete" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <p className="text-sm font-semibold">Planı Sil</p>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-muted-foreground">
                <span className="font-mono font-semibold text-foreground">"{plan.slug}"</span> planını kalıcı olarak silmek istediğinize emin misiniz?
              </p>
              {usageCount > 0 && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/8 px-4 py-3 space-y-1.5">
                  <p className="text-sm font-semibold text-amber-500">
                    Bu plana atanmış {usageCount} organizatör var.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Silmeden önce bu organizatörleri başka bir plana taşıyın.
                  </p>
                  <AppLink
                    to={paths.usersFilteredByPlan(plan.slug)}
                    onClick={closeModal}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-500 hover:underline cursor-pointer"
                  >
                    <Users className="h-3 w-3" />
                    Organizatörleri görüntüle →
                  </AppLink>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 pb-4">
              <button onClick={closeModal} className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted transition-colors cursor-pointer">İptal</button>
              <button onClick={confirmDelete} disabled={usageCount > 0}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-destructive text-white text-sm font-semibold hover:bg-destructive/90 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer">
                <Trash2 className="h-3.5 w-3.5" /> Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── History Modal ── */}
      {modal === "history" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div>
                <p className="text-sm font-semibold">Değişiklik Geçmişi</p>
                <p className="text-xs text-muted-foreground mt-0.5 font-mono">/{plan.slug}</p>
              </div>
              <button onClick={closeModal} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 py-4 max-h-96 overflow-y-auto">
              {loadingH ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
              ) : history.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8 italic">Değişiklik kaydı bulunamadı.</p>
              ) : (
                <div className="space-y-2">
                  {history.map((h) => {
                    // old_values = full DB row (snake_case), new_values = PATCH body (camelCase)
                    let oldRow: Record<string, unknown> = {};
                    let newBody: Record<string, unknown> = {};
                    try { if (h.old_values) oldRow = JSON.parse(h.old_values); } catch { /* ignore */ }
                    try { if (h.new_values) newBody = JSON.parse(h.new_values); } catch { /* ignore */ }

                    // Map camelCase body keys → snake_case DB columns + human labels
                    const FIELD_MAP: Record<string, { dbKey: string; label: string; fmt?: (v: unknown) => string }> = {
                      label:               { dbKey: "label",                  label: "Etiket" },
                      maxWeddings:         { dbKey: "max_weddings",            label: "Maks. Düğün (eski)" },
                      priceTry:            { dbKey: "price_try",               label: "Fiyat", fmt: (v) => (v === null || v === undefined ? "Teklif ile" : `₺${v}`) },
                      maxGuestsPerWedding: { dbKey: "max_guests_per_wedding",  label: "Maks. Misafir" },
                      maxStorageBytes:     { dbKey: "max_storage_bytes",       label: "Depolama", fmt: (v) => formatBytes(Number(v)) },
                      maxMediaPerWedding:  { dbKey: "max_media_per_wedding",   label: "Maks. Medya" },
                      maxReminderSends:    { dbKey: "max_reminder_sends",      label: "Tur Kotası" },
                      maxSmsSegments:      { dbKey: "max_sms_segments",        label: "SMS Adedi" },
                      sortOrder:           { dbKey: "sort_order",              label: "Sıra" },
                      isDefault:           { dbKey: "is_default",              label: "Varsayılan", fmt: (v) => v ? "Evet" : "Hayır" },
                    };

                    // For "updated": show only body fields that actually changed
                    const diffRows = h.change_type === "updated"
                      ? Object.entries(newBody)
                          .map(([camel, newVal]) => {
                            const meta = FIELD_MAP[camel];
                            if (!meta) return null;
                            const oldVal = oldRow[meta.dbKey];
                            if (String(oldVal ?? "") === String(newVal ?? "")) return null;
                            return {
                              label: meta.label,
                              old: meta.fmt ? meta.fmt(oldVal) : String(oldVal ?? "—"),
                              new: meta.fmt ? meta.fmt(newVal) : String(newVal ?? "—"),
                            };
                          })
                          .filter(Boolean) as { label: string; old: string; new: string }[]
                      : [];

                    return (
                      <div key={h.id} className="rounded-xl border border-border/50 overflow-hidden">
                        <div className="flex items-center gap-3 px-4 py-2.5 bg-muted/20">
                          <span className={cn("shrink-0 px-2 py-0.5 rounded-lg font-bold uppercase text-[10px] border",
                            h.change_type === "created" ? "bg-green-500/10 text-green-400 border-green-500/20"
                            : h.change_type === "deleted" ? "bg-red-500/10 text-red-400 border-red-500/20"
                            : "bg-blue-500/10 text-blue-400 border-blue-500/20")}>
                            {h.change_type}
                          </span>
                          <span className="text-xs text-muted-foreground flex-1 truncate">{h.changed_by}</span>
                          <span className="text-[10px] text-muted-foreground/50 whitespace-nowrap shrink-0">
                            {new Date(h.created_at + "Z").toLocaleString(lang)}
                          </span>
                        </div>
                        {diffRows.length > 0 && (
                          <div className="px-4 py-2.5 space-y-1.5">
                            {diffRows.map((row) => (
                              <div key={row.label} className="flex items-center gap-2 text-xs">
                                <span className="text-muted-foreground/60 w-28 shrink-0">{row.label}</span>
                                <span className="line-through text-muted-foreground/50">{row.old}</span>
                                <span className="text-muted-foreground/40">→</span>
                                <span className="text-foreground font-semibold">{row.new}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Card ── */}
      <div className={cn(
        "relative rounded-2xl border border-border bg-card overflow-hidden flex flex-col shadow-xl transition-shadow duration-300 hover:shadow-2xl",
        theme.glow,
        !plan.is_active && "opacity-50"
      )}>
        {/* Accent bar */}
        <div className={cn("h-1 w-full bg-gradient-to-r shrink-0", theme.accent)} />

        {/* Header: name + badge */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-3">
          <div>
            <p className="font-mono text-[10px] text-muted-foreground/50 mb-1">/{plan.slug}</p>
            <h3 className={cn("font-serif italic text-4xl font-bold leading-none", getPlanColors(plan.slug).name)}>
              {plan.label}
            </h3>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0 pt-1">
            {/* is_default sayı (0/1) — `0 && …` ekrana "0" basıyordu */}
            {!!plan.is_default && (
              <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border", theme.badge)}>
                Varsayılan
              </span>
            )}
            <span
              data-testid={`plan-visibility-${plan.slug}`}
              title={onSite ? t("plansAdmin.onSiteHint") : t("plansAdmin.privateHint")}
              className={cn(
                "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border",
                onSite
                  ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:text-emerald-400"
                  : "bg-muted text-muted-foreground border-border",
              )}
            >
              {onSite ? <Globe className="h-3 w-3" aria-hidden="true" /> : <Lock className="h-3 w-3" aria-hidden="true" />}
              <span>{onSite ? t("plansAdmin.onSite") : t("plansAdmin.private")}</span>
            </span>
            {!plan.is_active && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-muted text-muted-foreground border border-border">
                Pasif
              </span>
            )}
          </div>
        </div>

        {/* Fiyat + satılmış paket (paket modeli: düğün başına satılan şey bu tanım) */}
        <div className="px-6 pb-5 space-y-2">
          {hasPrice && (
            <p data-testid={`plan-price-${plan.slug}`} className="flex items-center gap-2 text-lg font-bold text-foreground">
              <Banknote className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              {plan.price_try === null ? t("plansAdmin.priceQuote") : formatTry(plan.price_try ?? 0, lang)}
            </p>
          )}
          <p className="flex items-baseline gap-2">
            <span data-testid={`plan-sold-${plan.slug}`} className="text-5xl font-black text-foreground tracking-tight">{sold}</span>
            <span className="text-sm text-muted-foreground">{t("plansAdmin.sold")}</span>
          </p>
          {usageCount > 0 && (
            <p className="text-xs text-muted-foreground">{t("plansAdmin.accountUsers", { n: usageCount })}</p>
          )}
        </div>

        {/* Divider */}
        <div className="mx-6 h-px bg-border" />

        {/* Limits */}
        <div className="px-6 py-5 grid grid-cols-2 gap-y-4 gap-x-6 flex-1">
          {([
            { icon: Users,     label: "Misafir/Düğün", val: plan.max_guests_per_wedding.toLocaleString() },
            { icon: HardDrive, label: "Depolama",       val: formatBytes(plan.max_storage_bytes) },
            { icon: Image,     label: "Medya/Düğün",   val: plan.max_media_per_wedding.toLocaleString() },
            { icon: Repeat,    label: "Tur/Düğün",     val: plan.max_reminder_sends },
            { icon: MessageSquareText, label: "SMS/Düğün", val: plan.max_sms_segments.toLocaleString() },
          ] as const).map(({ icon: Icon, label, val }) => (
            <div key={label} className="flex items-center gap-2.5">
              <div className={cn("p-1.5 rounded-lg shrink-0", theme.icon)}>
                <Icon className="h-3.5 w-3.5" />
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-none mb-0.5">{label}</p>
                <p className="text-sm font-bold text-foreground">{val}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border bg-muted/20 flex items-center gap-1">
          <button onClick={() => setModal("edit")} title="Düzenle"
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-primary/8 transition-colors cursor-pointer">
            <Pencil className="h-4 w-4" />
          </button>
          {!plan.is_default && !onSite && !!plan.is_active && (
            <button onClick={() => setModal("delete")} title="Sil" aria-label="Sil"
              className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/8 transition-colors cursor-pointer">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          {!plan.is_active && (
            <button onClick={() => void onReactivate(plan.id)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-primary hover:bg-primary/8 transition-colors cursor-pointer">
              <RotateCcw className="h-3.5 w-3.5" />
              <span>{t("plansAdmin.reactivate")}</span>
            </button>
          )}
          <button onClick={openHistory} title="Geçmiş"
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-primary/8 transition-colors cursor-pointer ml-auto">
            {loadingH ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </>
  );
}

function Toast({ msg, ok, onDone }: { msg: string; ok: boolean; onDone: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDone, 3200);
    return () => clearTimeout(timer);
  }, [onDone]);
  return (
    <div role="status" aria-live="polite" className={cn(
      "fixed bottom-6 left-1/2 -translate-x-1/2 z-[200]",
      "flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium",
      ok ? "bg-emerald-500 text-white" : "bg-red-500 text-white",
    )}>
      {ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
      <span>{msg}</span>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────
export function PlansPage() {
  const { t, i18n } = useTranslation();
  const [plans, setPlans]   = useState<PlanRow[]>([]);
  const [usage, setUsage]   = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const clearToast = useCallback(() => setToast(null), []);

  const fetch = useCallback(async () => {
    try {
      const [pr, ur] = await Promise.all([
        adminPlansApi.get<PlanRow[]>("/"),
        adminPlansApi.get<UsageCount[]>("/usage-count"),
      ]);
      setPlans(pr.data ?? []);
      const m: Record<string, number> = {};
      for (const u of ur.data ?? []) m[u.plan] = u.count;
      setUsage(m);
      setError("");
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  // Her aksiyon başarı + hata toast'u verir (eskiden hata sessizce yutuluyordu).
  const act = async (fn: () => Promise<unknown>, okKey: string, okMsg?: (res: unknown) => string | null) => {
    try {
      const res = await fn();
      setToast({ msg: okMsg?.(res) ?? t(okKey), ok: true });
      await fetch();
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setToast({ msg: PLAN_ERRORS[msg] ? t(PLAN_ERRORS[msg]) : t("plansAdmin.failed", { error: msg }), ok: false });
      return false;
    }
  };
  const handleCreate = async ({ price, ...d }: CreateForm) => {
    const body = { ...d, ...(d.isDefault ? { priceTry: 0 } : priceOf(price) !== null ? { priceTry: priceOf(price) } : {}) };
    if (await act(() => adminPlansApi.post("/", body), "plansAdmin.created")) setShowCreate(false);
  };
  const handleUpdate = async (id: string, d: object) => {
    await act(
      () => adminPlansApi.patch<{ sold: number }>(`/${id}`, d),
      "plansAdmin.updated",
      (res) => {
        // Satılmış paketler sözleşmedir — değişiklik yalnız yeni satışlara; bunu açıkça söyle.
        const n = (res as { data?: { sold?: number } })?.data?.sold ?? 0;
        return n > 0 ? t("plansAdmin.updatedNewSalesOnly", { n }) : null;
      },
    );
  };
  const handleDelete = async (id: string, _slug: string) => {
    await act(() => adminPlansApi.delete(`/${id}`), "plansAdmin.deleted");
  };
  const handleReactivate = async (id: string) => {
    await act(() => adminPlansApi.patch(`/${id}`, { isActive: true }), "plansAdmin.reactivated");
  };

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );

  return (
    <>
      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onSave={handleCreate} />}

      <div className="space-y-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif italic text-3xl text-foreground">{t("admin.plans", "Planlar")}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t("plansAdmin.subtitle")}</p>
          </div>
          <button onClick={() => setShowCreate(true)}
            className="shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors shadow-md shadow-primary/25 cursor-pointer">
            <Plus className="h-4 w-4" /> Yeni Plan
          </button>
        </div>

        {error && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>
        )}

        <p data-testid="plans-site-note" className="flex items-start gap-2 rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          <Info className="h-4 w-4 shrink-0 mt-0.5 text-primary" aria-hidden="true" />
          <span>{t("plansAdmin.siteNote")}</span>
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} usageCount={usage[plan.slug] ?? 0}
              onUpdate={handleUpdate} onDelete={handleDelete} onReactivate={handleReactivate} lang={i18n.language} />
          ))}
        </div>
      </div>
      {toast && <Toast msg={toast.msg} ok={toast.ok} onDone={clearToast} />}
    </>
  );
}
